import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Archive,
  Check,
  CirclePlus,
  LoaderCircle,
  Package,
  Pencil,
  Plus,
  Save,
  Tag,
} from 'lucide-react';
import { supabase } from '../../services/cloud/supabaseClient';
import { refreshServerAuthoritativeCatalog } from '../../services/sync/syncEngine';
import {
  PricingEngine,
  pricingTypes,
  addOnPricingTypes,
  serviceSchema,
} from '../../domain/pricing';
import type { PricingType, AddOnPricingType } from '../../domain/pricing';
import type { Database } from '../../types/supabase';

type ServiceRow = Database['public']['Tables']['services']['Row'];
type ServiceItemRow = Database['public']['Tables']['service_items']['Row'];
type AddonRow = Database['public']['Tables']['addons']['Row'];
type Tab = 'services' | 'addons';

const moneyText = (amount: number) => (amount / 100).toFixed(2);
const money = (amount: number) => PricingEngine.formatCentavos(amount);
const fieldClass =
  'mt-1.5 min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 outline-none focus:border-[#00549C] focus:ring-2 focus:ring-[#DCEEFF]';
const labelClass = 'block text-xs font-extrabold uppercase tracking-wide text-slate-500';

function parseMoney(value: string): number {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
    throw new Error('Enter a peso amount with no more than two decimal places.');
  }
  const centavos = Math.round(Number(normalized) * 100);
  if (!Number.isSafeInteger(centavos) || centavos <= 0) {
    throw new Error('Price must be greater than ₱0.00.');
  }
  return centavos;
}

function parsePositiveNumber(value: string, label: string): number {
  const result = Number(value);
  if (!Number.isFinite(result) || result <= 0 || Math.round(result * 1000) !== result * 1000) {
    throw new Error(`${label} must be a positive number with at most three decimal places.`);
  }
  return result;
}

function statusBadge(active: boolean) {
  return active ? (
    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-emerald-700">
      Active
    </span>
  ) : (
    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-slate-500">
      Archived
    </span>
  );
}

export function CatalogManagement({ branchId }: { branchId: string | null }) {
  const [tab, setTab] = useState<Tab>('services');
  const [services, setServices] = useState<ServiceRow[]>([]);
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [serviceItems, setServiceItems] = useState<ServiceItemRow[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [serviceMode, setServiceMode] = useState<PricingType>('PER_LOAD');
  const [editingItem, setEditingItem] = useState<ServiceItemRow | null>(null);
  const [editingAddonId, setEditingAddonId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadCatalog = useCallback(async () => {
    setError('');
    const [servicesResult, addonsResult] = await Promise.all([
      supabase.from('services').select('*').order('sort_order'),
      supabase.from('addons').select('*').order('sort_order'),
    ]);
    if (servicesResult.error) throw servicesResult.error;
    if (addonsResult.error) throw addonsResult.error;
    setServices(servicesResult.data);
    setAddons(addonsResult.data);
    if (selectedServiceId) {
      const itemsResult = await supabase
        .from('service_items')
        .select('*')
        .eq('service_id', selectedServiceId)
        .order('sort_order');
      if (itemsResult.error) throw itemsResult.error;
      setServiceItems(itemsResult.data);
    } else setServiceItems([]);
  }, [selectedServiceId]);

  useEffect(() => {
    void loadCatalog().catch((cause: unknown) =>
      setError(cause instanceof Error ? cause.message : 'Unable to load the catalog.'),
    );
  }, [loadCatalog]);

  const selectedService = services.find((service) => service.id === selectedServiceId) ?? null;
  const orderedServices = useMemo(
    () => services.filter((service) => showArchived || service.active),
    [services, showArchived],
  );
  const orderedAddons = useMemo(
    () => addons.filter((addon) => showArchived || addon.active),
    [addons, showArchived],
  );

  const afterSave = async (successMessage: string) => {
    await refreshServerAuthoritativeCatalog();
    await loadCatalog();
    window.dispatchEvent(new CustomEvent('babs:sync-changed'));
    setMessage(successMessage);
    setError('');
  };

  const saveService = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!branchId) {
      setError(
        'This management account is not assigned to a branch. Ask the Owner to assign a branch before editing the catalog.',
      );
      return;
    }
    const form = new FormData(event.currentTarget);
    const pricingType = String(form.get('pricingType')) as PricingType;
    const id = editingServiceId ?? crypto.randomUUID();
    const isPerItem = pricingType === 'PER_ITEM';
    try {
      setBusy(true);
      const parsed = serviceSchema.safeParse({
        id,
        name: String(form.get('name') ?? '').trim(),
        category: String(form.get('category') ?? '').trim(),
        description: String(form.get('description') ?? '').trim() || undefined,
        pricingType,
        priceCentavos: isPerItem ? 0 : parseMoney(String(form.get('price') ?? '')),
        loadCapacityKg:
          pricingType === 'PER_LOAD'
            ? parsePositiveNumber(String(form.get('loadCapacityKg') ?? ''), 'Load capacity')
            : undefined,
        minimumKg:
          pricingType === 'PER_KG' && String(form.get('minimumKg') ?? '').trim()
            ? parsePositiveNumber(String(form.get('minimumKg')), 'Minimum KG')
            : undefined,
        requiresWeight: pricingType === 'PER_LOAD' || pricingType === 'PER_KG',
        requiresItemSelection: isPerItem,
        allowsQuantity: true,
        allowsAddons: form.get('allowsAddons') === 'on',
        active: form.get('active') === 'on',
        sortOrder: Number(form.get('sortOrder')),
      });
      if (!parsed.success)
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the service details.');
      const value = parsed.data;
      const result = await supabase.from('services').upsert({
        id: value.id,
        branch_id: branchId,
        name: value.name,
        category: value.category ?? 'Laundry',
        description: value.description ?? null,
        pricing_type: value.pricingType,
        price_centavos: value.priceCentavos,
        minimum_kg: value.minimumKg ?? null,
        load_capacity_kg: value.loadCapacityKg ?? null,
        requires_weight: value.requiresWeight ?? false,
        requires_item_selection: value.requiresItemSelection ?? false,
        allows_quantity: value.allowsQuantity ?? true,
        allows_addons: value.allowsAddons ?? true,
        active: value.active ?? true,
        sort_order: value.sortOrder ?? 0,
      });
      if (result.error) throw result.error;
      setEditingServiceId(value.id);
      setSelectedServiceId(value.id);
      await afterSave('Service saved to Supabase and synced to this POS.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save service.');
    } finally {
      setBusy(false);
    }
  };

  const saveServiceItem = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedService) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const itemId = editingItem?.id ?? crypto.randomUUID();
    try {
      setBusy(true);
      const name = String(form.get('itemName') ?? '').trim();
      if (!name) throw new Error('Enter an item name.');
      const unitPriceCentavos = parseMoney(String(form.get('itemPrice') ?? ''));
      const sortOrder = Number(form.get('itemSortOrder'));
      if (!Number.isInteger(sortOrder) || sortOrder < 0)
        throw new Error('Display order must be a whole number starting at 0.');
      const result = await supabase.from('service_items').upsert({
        id: itemId,
        service_id: selectedService.id,
        name,
        unit_price_centavos: unitPriceCentavos,
        active: editingItem?.active ?? true,
        sort_order: sortOrder,
      });
      if (result.error) throw result.error;
      setEditingItem(null);
      await afterSave('Service item saved.');
      formElement.reset();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save service item.');
    } finally {
      setBusy(false);
    }
  };

  const saveAddon = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!branchId) {
      setError(
        'This management account is not assigned to a branch. Ask the Owner to assign a branch before editing the catalog.',
      );
      return;
    }
    const form = new FormData(event.currentTarget);
    try {
      setBusy(true);
      const name = String(form.get('name') ?? '').trim();
      const category = String(form.get('category') ?? '').trim();
      const sortOrder = Number(form.get('sortOrder'));
      const pricingType = String(form.get('pricingType')) as AddOnPricingType;
      if (!name || !category) throw new Error('Name and category are required.');
      if (!addOnPricingTypes.includes(pricingType))
        throw new Error('Choose a supported add-on pricing mode.');
      if (!Number.isInteger(sortOrder) || sortOrder < 0)
        throw new Error('Display order must be a whole number starting at 0.');
      const result = await supabase.from('addons').upsert({
        id: editingAddonId ?? crypto.randomUUID(),
        branch_id: branchId,
        name,
        category,
        pricing_type: pricingType,
        price_centavos: parseMoney(String(form.get('price') ?? '')),
        active: form.get('active') === 'on',
        sort_order: sortOrder,
      });
      if (result.error) throw result.error;
      setEditingAddonId(null);
      await afterSave('Add-on saved to Supabase and synced to this POS.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save add-on.');
    } finally {
      setBusy(false);
    }
  };

  const serviceForEdit = services.find((service) => service.id === editingServiceId);
  const addonForEdit = addons.find((addon) => addon.id === editingAddonId);

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-5 py-5 md:px-7">
        <div className="flex items-start gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-[#DCEEFF] text-[#00549C]">
            <Package size={23} />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#00549C]">
              Owner / Manager
            </p>
            <h2 className="text-xl font-black text-[#003B70]">Service Catalog</h2>
            <p className="mt-1 max-w-2xl text-sm font-medium text-slate-500">
              Supabase is authoritative. Changes sync to POS devices when online; each completed
              sale keeps its original price and service details.
            </p>
          </div>
        </div>
        <label className="flex min-h-10 items-center gap-2 rounded-xl bg-slate-50 px-3 text-xs font-bold text-slate-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="h-4 w-4 accent-[#00549C]"
          />
          Include archived
        </label>
      </header>

      <div className="flex gap-2 border-b border-slate-100 px-5 py-3 md:px-7">
        {(['services', 'addons'] as const).map((key) => (
          <button
            key={key}
            onClick={() => {
              setTab(key);
              setMessage('');
              setError('');
            }}
            className={`min-h-10 rounded-xl px-4 text-sm font-black capitalize ${tab === key ? 'bg-[#00549C] text-white' : 'bg-slate-50 text-slate-600'}`}
          >
            {key === 'addons' ? 'Add-ons' : 'Services'}
          </button>
        ))}
        <span className="ml-auto hidden items-center gap-1 text-xs font-semibold text-slate-400 sm:flex">
          <Tag size={14} /> No code or redeploy needed for catalog edits
        </span>
      </div>

      {message && (
        <div
          role="status"
          className="mx-5 mt-4 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800 md:mx-7"
        >
          <Check size={17} />
          {message}
        </div>
      )}
      {error && (
        <div
          role="alert"
          className="mx-5 mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700 md:mx-7"
        >
          {error}
        </div>
      )}

      {tab === 'services' ? (
        <div className="grid gap-5 p-5 md:p-7 xl:grid-cols-[minmax(0,1fr)_minmax(350px,0.85fr)]">
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-black text-[#003B70]">
                  {showArchived ? 'All services' : 'Active catalog'}
                </h3>
                <p className="text-xs font-semibold text-slate-400">
                  {orderedServices.length} service{orderedServices.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingServiceId(null);
                  setSelectedServiceId(null);
                  setServiceMode('PER_LOAD');
                  setEditingItem(null);
                }}
                className="flex min-h-10 items-center gap-2 rounded-xl bg-[#DCEEFF] px-3 text-sm font-black text-[#00549C]"
              >
                <CirclePlus size={17} /> Add service
              </button>
            </div>
            <div className="space-y-2">
              {orderedServices.map((service) => (
                <button
                  key={service.id}
                  onClick={() => {
                    setSelectedServiceId(service.id);
                    setEditingServiceId(service.id);
                    setServiceMode(service.pricing_type);
                    setEditingItem(null);
                    setMessage('');
                    setError('');
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-2xl border p-4 text-left transition ${service.id === editingServiceId ? 'border-[#78AEE0] bg-[#F4F9FE]' : 'border-slate-200 bg-white hover:border-[#B5D5EF]'}`}
                >
                  <span className="min-w-0">
                    <span className="flex flex-wrap items-center gap-2 font-extrabold text-slate-800">
                      {service.name}
                      {statusBadge(service.active)}
                    </span>
                    <span className="mt-1 block truncate text-xs font-semibold text-slate-500">
                      {service.category} · {service.pricing_type.replace('_', ' ')} ·{' '}
                      {service.pricing_type === 'PER_ITEM'
                        ? 'item prices'
                        : money(service.price_centavos)}
                      {service.pricing_type === 'PER_LOAD' && service.load_capacity_kg
                        ? ` / load up to ${service.load_capacity_kg} KG`
                        : ''}
                    </span>
                  </span>
                  <Pencil size={16} className="shrink-0 text-[#00549C]" />
                </button>
              ))}
              {orderedServices.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm font-semibold text-slate-400">
                  No services yet. Add the first service to begin.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[#FAFCFE] p-4 md:p-5">
            <div className="mb-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#00549C]">
                {serviceForEdit ? 'Edit service' : 'New service'}
              </p>
              <p className="mt-1 text-lg font-black text-[#003B70]">
                {serviceForEdit?.name ?? 'Add to your catalog'}
              </p>
            </div>
            <form onSubmit={saveService} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Service name
                  <input
                    className={fieldClass}
                    name="name"
                    key={`service-name-${editingServiceId ?? 'new'}`}
                    defaultValue={serviceForEdit?.name ?? ''}
                    required
                    maxLength={80}
                  />
                </label>
                <label className={labelClass}>
                  Category
                  <input
                    className={fieldClass}
                    name="category"
                    key={`service-cat-${editingServiceId ?? 'new'}`}
                    defaultValue={serviceForEdit?.category ?? 'Laundry'}
                    required
                    maxLength={50}
                  />
                </label>
              </div>
              <label className={labelClass}>
                Description (optional)
                <input
                  className={fieldClass}
                  name="description"
                  key={`service-desc-${editingServiceId ?? 'new'}`}
                  defaultValue={serviceForEdit?.description ?? ''}
                  maxLength={160}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Pricing mode
                  <select
                    className={fieldClass}
                    name="pricingType"
                    value={serviceMode}
                    onChange={(e) => setServiceMode(e.target.value as PricingType)}
                  >
                    {pricingTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>
                  Display order
                  <input
                    className={fieldClass}
                    name="sortOrder"
                    type="number"
                    min="0"
                    step="1"
                    key={`service-order-${editingServiceId ?? 'new'}`}
                    defaultValue={serviceForEdit?.sort_order ?? services.length + 1}
                    required
                  />
                </label>
              </div>
              {serviceForEdit?.pricing_type === 'PER_LOAD' ? null : null}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Price (₱)
                  <input
                    className={fieldClass}
                    name="price"
                    type="number"
                    min="0.01"
                    step="0.01"
                    key={`service-price-${editingServiceId ?? 'new'}`}
                    defaultValue={
                      serviceForEdit && serviceForEdit.pricing_type !== 'PER_ITEM'
                        ? moneyText(serviceForEdit.price_centavos)
                        : ''
                    }
                    required={serviceMode !== 'PER_ITEM'}
                    disabled={serviceMode === 'PER_ITEM'}
                    placeholder={serviceMode === 'PER_ITEM' ? 'Set item prices below' : '0.00'}
                  />
                </label>
                <label className={labelClass}>
                  Load capacity (KG)
                  <input
                    className={fieldClass}
                    name="loadCapacityKg"
                    type="number"
                    min="0.001"
                    step="0.001"
                    key={`service-load-${editingServiceId ?? 'new'}`}
                    defaultValue={
                      serviceForEdit?.load_capacity_kg ?? (serviceMode === 'PER_LOAD' ? 7 : '')
                    }
                    required={serviceMode === 'PER_LOAD'}
                    disabled={serviceMode !== 'PER_LOAD'}
                    placeholder="7"
                  />
                </label>
              </div>
              <label className={labelClass}>
                Minimum weight for per-KG pricing (optional)
                <input
                  className={fieldClass}
                  name="minimumKg"
                  type="number"
                  min="0.001"
                  step="0.001"
                  key={`service-min-${editingServiceId ?? 'new'}`}
                  defaultValue={serviceForEdit?.minimum_kg ?? ''}
                  disabled={serviceMode !== 'PER_KG'}
                  placeholder="No minimum"
                />
              </label>
              {serviceMode === 'PER_ITEM' && (
                <p className="rounded-xl bg-amber-50 p-3 text-xs font-semibold text-amber-800">
                  Per-item services are priced by their laundry-item list. Add at least one
                  positively priced item below before offering it to cashiers.
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-2 py-1">
                <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                  <input
                    type="checkbox"
                    name="active"
                    key={`service-active-${editingServiceId ?? 'new'}`}
                    defaultChecked={serviceForEdit?.active ?? true}
                    className="h-4 w-4 accent-[#00549C]"
                  />{' '}
                  Active for POS
                </label>
                <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
                  <input
                    type="checkbox"
                    name="allowsAddons"
                    key={`service-addons-${editingServiceId ?? 'new'}`}
                    defaultChecked={serviceForEdit?.allows_addons ?? true}
                    className="h-4 w-4 accent-[#00549C]"
                  />{' '}
                  Allow add-ons
                </label>
              </div>
              <button
                disabled={busy}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#00549C] px-4 text-sm font-black text-white disabled:opacity-50"
              >
                <Save size={16} />
                {busy ? 'Saving…' : 'Save service'}
              </button>
            </form>

            {serviceMode === 'PER_ITEM' && selectedService && (
              <div className="mt-6 border-t border-slate-200 pt-5">
                <div className="mb-3">
                  <h4 className="font-black text-[#003B70]">Items & item prices</h4>
                  <p className="text-xs font-semibold text-slate-400">
                    Archive an item instead of deleting it.
                  </p>
                </div>
                <div className="mb-3 space-y-2">
                  {serviceItems
                    .filter((item) => showArchived || item.active)
                    .map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between gap-3 rounded-xl bg-white p-3 text-sm"
                      >
                        <span className="min-w-0">
                          <b className="text-slate-800">{item.name}</b>
                          <span className="ml-2 text-xs font-bold text-[#00549C]">
                            {money(item.unit_price_centavos)}
                          </span>
                          {!item.active && (
                            <span className="ml-2 text-[10px] font-black uppercase text-slate-400">
                              Archived
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingItem(item)}
                            aria-label={`Edit ${item.name}`}
                            className="grid h-9 w-9 place-items-center rounded-lg bg-slate-50 text-slate-500"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setError('');
                              void (async () => {
                                setBusy(true);
                                const result = await supabase
                                  .from('service_items')
                                  .update({ active: !item.active })
                                  .eq('id', item.id);
                                if (result.error) setError(result.error.message);
                                else
                                  await afterSave(
                                    item.active ? 'Item archived.' : 'Item reactivated.',
                                  );
                                setBusy(false);
                              })();
                            }}
                            aria-label={`${item.active ? 'Archive' : 'Reactivate'} ${item.name}`}
                            className="grid h-9 w-9 place-items-center rounded-lg bg-slate-50 text-slate-500"
                          >
                            <Archive size={15} />
                          </button>
                        </span>
                      </div>
                    ))}
                </div>
                <form
                  onSubmit={saveServiceItem}
                  className="grid gap-2 sm:grid-cols-[1fr_110px_75px_auto] sm:items-end"
                >
                  <input
                    name="itemName"
                    placeholder="Item name"
                    className={fieldClass}
                    required
                    maxLength={80}
                    defaultValue={editingItem?.name ?? ''}
                    key={`item-name-${editingItem?.id ?? 'new'}`}
                  />
                  <input
                    name="itemPrice"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="Price ₱"
                    className={fieldClass}
                    required
                    defaultValue={editingItem ? moneyText(editingItem.unit_price_centavos) : ''}
                    key={`item-price-${editingItem?.id ?? 'new'}`}
                  />
                  <input
                    name="itemSortOrder"
                    type="number"
                    min="0"
                    step="1"
                    defaultValue={editingItem?.sort_order ?? serviceItems.length + 1}
                    aria-label="Item display order"
                    className={fieldClass}
                    required
                    key={`item-order-${editingItem?.id ?? 'new'}`}
                  />
                  <button
                    disabled={busy}
                    aria-label={editingItem ? 'Save service item' : 'Add service item'}
                    className="grid min-h-11 place-items-center rounded-xl bg-[#DCEEFF] px-4 font-black text-[#00549C]"
                  >
                    <Plus size={18} />
                  </button>
                </form>
                {editingItem && (
                  <button
                    type="button"
                    onClick={() => setEditingItem(null)}
                    className="mt-2 text-xs font-bold text-slate-500"
                  >
                    Cancel item edit
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid gap-5 p-5 md:p-7 xl:grid-cols-[minmax(0,1fr)_minmax(350px,0.8fr)]">
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="font-black text-[#003B70]">
                  {showArchived ? 'All add-ons' : 'Active add-ons'}
                </h3>
                <p className="text-xs font-semibold text-slate-400">
                  {orderedAddons.length} add-on{orderedAddons.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                onClick={() => setEditingAddonId(null)}
                className="flex min-h-10 items-center gap-2 rounded-xl bg-[#DCEEFF] px-3 text-sm font-black text-[#00549C]"
              >
                <CirclePlus size={17} /> Add add-on
              </button>
            </div>
            <div className="space-y-2">
              {orderedAddons.map((addon) => (
                <button
                  key={addon.id}
                  onClick={() => {
                    setEditingAddonId(addon.id);
                    setMessage('');
                    setError('');
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-2xl border p-4 text-left ${addon.id === editingAddonId ? 'border-[#78AEE0] bg-[#F4F9FE]' : 'border-slate-200 bg-white hover:border-[#B5D5EF]'}`}
                >
                  <span>
                    <span className="flex flex-wrap items-center gap-2 font-extrabold text-slate-800">
                      {addon.name}
                      {statusBadge(addon.active)}
                    </span>
                    <span className="mt-1 block text-xs font-semibold text-slate-500">
                      {addon.category} · {addon.pricing_type.replace('_', ' ')} ·{' '}
                      {money(addon.price_centavos)}
                    </span>
                  </span>
                  <Pencil size={16} className="shrink-0 text-[#00549C]" />
                </button>
              ))}
              {orderedAddons.length === 0 && (
                <p className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm font-semibold text-slate-400">
                  No add-ons yet. Add the first add-on.
                </p>
              )}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-[#FAFCFE] p-4 md:p-5">
            <div className="mb-4">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#00549C]">
                {addonForEdit ? 'Edit add-on' : 'New add-on'}
              </p>
              <p className="mt-1 text-lg font-black text-[#003B70]">
                {addonForEdit?.name ?? 'Add an optional extra'}
              </p>
            </div>
            <form onSubmit={saveAddon} className="space-y-3">
              <label className={labelClass}>
                Name
                <input
                  className={fieldClass}
                  name="name"
                  key={`addon-name-${editingAddonId ?? 'new'}`}
                  defaultValue={addonForEdit?.name ?? ''}
                  required
                  maxLength={80}
                />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Category
                  <input
                    className={fieldClass}
                    name="category"
                    key={`addon-cat-${editingAddonId ?? 'new'}`}
                    defaultValue={addonForEdit?.category ?? 'Laundry Add-ons'}
                    required
                    maxLength={50}
                  />
                </label>
                <label className={labelClass}>
                  Price (₱)
                  <input
                    className={fieldClass}
                    name="price"
                    type="number"
                    min="0.01"
                    step="0.01"
                    key={`addon-price-${editingAddonId ?? 'new'}`}
                    defaultValue={addonForEdit ? moneyText(addonForEdit.price_centavos) : ''}
                    required
                  />
                </label>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  Pricing mode
                  <select
                    className={fieldClass}
                    name="pricingType"
                    key={`addon-mode-${editingAddonId ?? 'new'}`}
                    defaultValue={addonForEdit?.pricing_type ?? 'FLAT'}
                  >
                    {addOnPricingTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={labelClass}>
                  Display order
                  <input
                    className={fieldClass}
                    name="sortOrder"
                    type="number"
                    min="0"
                    step="1"
                    key={`addon-order-${editingAddonId ?? 'new'}`}
                    defaultValue={addonForEdit?.sort_order ?? addons.length + 1}
                    required
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 py-1 text-xs font-bold text-slate-600">
                <input
                  type="checkbox"
                  name="active"
                  key={`addon-active-${editingAddonId ?? 'new'}`}
                  defaultChecked={addonForEdit?.active ?? true}
                  className="h-4 w-4 accent-[#00549C]"
                />{' '}
                Active for POS
              </label>
              <button
                disabled={busy}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#00549C] px-4 text-sm font-black text-white disabled:opacity-50"
              >
                <Save size={16} />
                {busy ? 'Saving…' : 'Save add-on'}
              </button>
            </form>
            <p className="mt-4 rounded-xl bg-slate-100 p-3 text-xs font-semibold leading-relaxed text-slate-600">
              Inactive add-ons remain in the catalog for historical orders. Changes are audited
              automatically and will sync to connected cashier devices.
            </p>
          </div>
        </div>
      )}
      {busy && (
        <div className="flex items-center justify-center gap-2 border-t border-slate-100 py-3 text-xs font-bold text-slate-500">
          <LoaderCircle size={15} className="animate-spin" />
          Saving your catalog change…
        </div>
      )}
    </section>
  );
}
